import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { RiDownload2Line, RiEyeLine } from "@remixicon/react";
import {
  getTutorsForPay,
  getCycleBookings,
  getCyclePayouts,
  getPayoutHistory,
  uploadPayoutReceipt,
  openReceipt,
  recordCyclePayout,
  undoPayout,
} from "../../services/payouts";
import { getPayCycleVersions, getUsersByIds } from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import {
  amountProblem,
  cycleLessons,
  cyclePayRows,
  cycleSummary,
  cycleTeaching,
  owedMoves,
  owedNow,
  PAY_TABS,
  payableCycles,
  payoutCycleLabel,
  payoutsCsv,
  payRowsShown,
  payTabCounts,
  pickCycle,
  RECEIPT_TYPES,
  receiptProblem,
  round2,
} from "../../utils/cyclePay";
import { hoursText } from "../../utils/tutorPresentation";
import { dialable, egp } from "../../utils/queuePresentation";
import { countText, dateTimeLines } from "../../utils/kitPresentation";
import { headButtonSx, tokens } from "../../utils/figmaTokens";
import { ListHead } from "../../components/kit/PageHead";
import { TableCard } from "../../components/kit/SectionCard";
import DetailRows from "../../components/kit/DetailRows";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import SearchField from "../../components/kit/SearchField";
import SegmentTabs from "../../components/kit/SegmentTabs";
import ToolbarMenu from "../../components/kit/ToolbarMenu";
import StatusChip from "../../components/kit/StatusChip";
import SmallButton from "../../components/kit/SmallButton";
import { RowAction, SkeletonRows, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import CycleCard from "./CycleCard";
import PayoutPanel from "./PayoutPanel";

// Money → Payouts (spec 053; Figma's look, 044 D114–D125). Pay a real tutor for one ended cycle,
// with the transfer receipt the callable checks; see each tutor's pay in a side panel the link
// carries (`?cycle=&tutor=`, D120); undo a mistake (DELETE_FINANCIAL). Every read goes through
// services/payouts.js — tutors by role, one cycle's bookings and payouts, the history capped at
// 100 — never a whole-collection scan.

const DIALOG_PAPER_SX = { sx: { borderRadius: "8px" } };
const ACTION_BUTTON_SX = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };
const COLUMNS = ["Tutor", "Lessons", "Teaching", "Fixed", "Total", "Paid", "Status", ""];
const NOTE_MAX = 2000;
const REASON_MAX = 500;
const HISTORY_BASE_COLUMNS = ["When", "Tutor", "Cycle", "Amount", "Receipt", "Owed before", "Owed after", "Notes", "By"];

// Table Cell "User" with 053's two grey lines: the email, then the phone as a tel: link. The link
// must not also open the panel the row opens.
const TutorCell = ({ tutor }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
    <InitialsAvatar name={tutor.name || tutor.id} colorKey={tutor.id} size={32} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="bodySmallStrong" component="div" noWrap sx={{ color: tokens.text.primary }}>
        {tutor.name || tutor.id}
      </Typography>
      <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
        {tutor.email || "—"}
      </Typography>
      <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
        {tutor.phone ? (
          <Link href={`tel:${dialable(tutor.phone)}`} onClick={(e) => e.stopPropagation()}>
            {tutor.phone}
          </Link>
        ) : (
          "—"
        )}
      </Typography>
    </Box>
  </Box>
);

const Payouts = () => {
  const { user } = useAuth();
  const canPay = hasPermission(user, permissions.EDIT_FINANCIAL);
  const canUndo = hasPermission(user, permissions.DELETE_FINANCIAL);
  const canOpenTutor = hasPermission(user, permissions.VIEW_TUTORS);

  const [searchParams, setSearchParams] = useSearchParams();
  const cycleParam = searchParams.get("cycle");
  const openTutorId = searchParams.get("tutor");

  const [cycles, setCycles] = useState([]);
  const [cyclesReady, setCyclesReady] = useState(false);
  const cycle = useMemo(() => pickCycle(cycles, cycleParam), [cycles, cycleParam]);

  const [tutors, setTutors] = useState(null);
  const [tutorsError, setTutorsError] = useState("");
  const tutorsLoading = tutors === null && !tutorsError;

  const [bookings, setBookings] = useState(null);
  const [cyclePayouts, setCyclePayouts] = useState(null);
  // The cycle those two were read for: a new cycle's rows wait for its own reads, so they never
  // mix the last cycle's lessons with the new one's key (R69).
  const [dataKey, setDataKey] = useState(null);
  const [cycleError, setCycleError] = useState("");
  const cycleLoading = (bookings === null || cyclePayouts === null || dataKey !== cycle?.key) && !cycleError;

  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState("");
  const [creatorNames, setCreatorNames] = useState({});
  const historyLoading = history === null && !historyError;

  const [toast, setToast] = useState("");

  const loadCycles = useCallback(async () => {
    try {
      const versions = await getPayCycleVersions();
      setCycles(payableCycles(versions, new Date()));
    } catch (e) {
      console.error("Payouts: cycles failed:", e);
      setCycles([]);
    } finally {
      setCyclesReady(true);
    }
  }, []);

  // A later load wins: an older answer must not replace the one from after a payment.
  const tutorsLoad = useRef(0);
  const loadTutors = useCallback(async () => {
    const mine = ++tutorsLoad.current;
    setTutors(null);
    setTutorsError("");
    try {
      const rows = await getTutorsForPay();
      if (mine !== tutorsLoad.current) return;
      setTutors(rows);
    } catch (e) {
      if (mine !== tutorsLoad.current) return;
      console.error("Payouts: tutors failed:", e);
      setTutorsError("Could not load tutors.");
    }
  }, []);

  const historyLoad = useRef(0);
  const loadHistory = useCallback(async () => {
    const mine = ++historyLoad.current;
    setHistory(null);
    setHistoryError("");
    try {
      const rows = await getPayoutHistory(100);
      if (mine !== historyLoad.current) return;
      setHistory(rows);
      const ids = [...new Set(rows.map((p) => p.createdBy).filter(Boolean))];
      if (ids.length) {
        getUsersByIds(ids)
          .then((names) => {
            if (mine === historyLoad.current) setCreatorNames(names);
          })
          .catch((e) => console.error("Payouts: creator names failed:", e));
      }
    } catch (e) {
      if (mine !== historyLoad.current) return;
      console.error("Payouts: history failed:", e);
      setHistoryError("Could not load payout history.");
    }
  }, []);

  const cycleLoad = useRef(0);
  const loadCycleData = useCallback(async (target) => {
    if (!target) return;
    const mine = ++cycleLoad.current;
    setBookings(null);
    setCyclePayouts(null);
    setCycleError("");
    try {
      const [b, p] = await Promise.all([getCycleBookings(target), getCyclePayouts(target.key)]);
      if (mine !== cycleLoad.current) return;
      setBookings(b);
      setCyclePayouts(p);
      setDataKey(target.key);
    } catch (e) {
      if (mine !== cycleLoad.current) return;
      console.error("Payouts: cycle data failed:", e);
      setCycleError("Could not load this cycle's pay.");
    }
  }, []);

  useEffect(() => {
    loadCycles();
    loadTutors();
    loadHistory();
  }, [loadCycles, loadTutors, loadHistory]);

  useEffect(() => {
    if (cycle) loadCycleData(cycle);
  }, [cycle, loadCycleData]);

  const reloadCycle = useCallback(() => loadCycleData(cycle), [loadCycleData, cycle]);

  const tutorsById = useMemo(() => Object.fromEntries((tutors || []).map((t) => [t.id, t])), [tutors]);

  const rows = useMemo(() => {
    if (!tutors || !bookings || !cyclePayouts || !cycle || dataKey !== cycle.key) return [];
    return cyclePayRows({
      tutors,
      teaching: cycleTeaching(bookings, tutorsById),
      lessons: cycleLessons(bookings, tutorsById),
      payouts: cyclePayouts,
      cycleKey: cycle.key,
    });
  }, [tutors, bookings, cyclePayouts, cycle, tutorsById, dataKey]);

  const owed = useMemo(() => owedNow(tutors), [tutors]);

  const rowsLoading = tutorsLoading || cycleLoading;
  const rowsFailed = Boolean(tutorsError) || Boolean(cycleError);
  const rowsReady = !rowsLoading && !rowsFailed;
  const retryRows = () => {
    if (tutorsError) loadTutors();
    if (cycleError) reloadCycle();
  };

  // ── Tabs and search (D116, D117) ──
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const shown = useMemo(() => payRowsShown(rows, tab, search), [rows, tab, search]);
  const summary = rowsReady ? cycleSummary(rows) : null;

  const owedView = tutorsError
    ? { value: "—", label: "Owed now · could not load" }
    : tutors === null
    ? { value: "—", label: "Owed now" }
    : { value: egp(owed.total), label: `Owed now · across ${countText(owed.count)} ${owed.count === 1 ? "tutor" : "tutors"}` };

  const headLine = !cycle
    ? "Pay each tutor for a pay cycle that has ended, with the transfer receipt"
    : rowsReady
    ? `Pay for ${cycle.label} · ${countText(rows.length)} ${rows.length === 1 ? "tutor" : "tutors"}`
    : `Pay for ${cycle.label}`;

  // ── The link (D120): opening the panel adds a history entry, so Back closes it; closing it and
  // choosing a cycle replace the entry. ──
  const changeParams = (change, replace) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        change(next);
        return next;
      },
      { replace }
    );
  const chooseCycle = (key) =>
    changeParams((p) => {
      p.set("cycle", key);
      p.delete("tutor");
    }, true);
  const openPanel = (tutorId) =>
    changeParams((p) => {
      if (cycle) p.set("cycle", cycle.key);
      p.set("tutor", tutorId);
    }, false);
  const closePanel = () => changeParams((p) => p.delete("tutor"), true);

  // The panel's row and the cycle it belongs to. It stays while the page reloads after a payment
  // or an undo (the rows are empty for that moment, D122), shows only under its own cycle (R69),
  // and goes once the link names no tutor.
  const [panel, setPanel] = useState(null);
  useEffect(() => {
    if (!openTutorId) setPanel(null);
    else if (!rowsLoading && cycle) setPanel({ key: cycle.key, row: rows.find((r) => r.tutor.id === openTutorId) || null });
  }, [openTutorId, rows, rowsLoading, cycle]);
  const panelRow = panel && panel.key === cycle?.key ? panel.row : null;

  // ── Export (D124) ──
  const doExport = () => {
    const { text, filename } = payoutsCsv(rows, cycle, new Date());
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const internalIds = useMemo(
    () => new Set((tutors || []).filter((t) => t.isInternal).map((t) => t.id)),
    [tutors]
  );
  const historyRows = useMemo(
    () => (history || []).filter((p) => !internalIds.has(p.teacherId)),
    [history, internalIds]
  );
  const historyColumns = canUndo ? [...HISTORY_BASE_COLUMNS, "Undo"] : HISTORY_BASE_COLUMNS;

  // ── Mark as paid (053, unchanged; opened from the panel, D122) ──
  const [payTutorId, setPayTutorId] = useState(null);
  const payRow = useMemo(() => rows.find((r) => r.tutor.id === payTutorId) || null, [rows, payTutorId]);
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [file, setFile] = useState(null);
  const [fileTouched, setFileTouched] = useState(false);
  const [uploadedPath, setUploadedPath] = useState(null);
  const fileInput = useRef(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const openPay = (row) => {
    setPayTutorId(row.tutor.id);
    setAmount(String(row.status.left));
    setAmountTouched(false);
    setFile(null);
    setFileTouched(false);
    setUploadedPath(null);
    setNote("");
    setSaveError("");
  };
  const closePay = () => {
    if (saving) return;
    setPayTutorId(null);
    // A failed save may still have landed (a lost answer): show what is really there first.
    if (saveError) {
      loadTutors();
      reloadCycle();
      loadHistory();
    }
  };

  const save = async () => {
    if (!payRow) return;
    const problem = amountProblem(amount, payRow.status.left) || receiptProblem(file);
    if (problem) {
      setAmountTouched(true);
      setFileTouched(true);
      setSaveError(problem);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const receiptPath = uploadedPath || (await uploadPayoutReceipt(payRow.tutor.id, file));
      setUploadedPath(receiptPath);
      await recordCyclePayout({
        teacherId: payRow.tutor.id,
        cycleKey: cycle.key,
        amount: round2(Number(amount)),
        receiptPath,
        receiptName: file.name.slice(0, 200),
        notes: note.trim() || undefined,
      });
      const paidName = payRow.tutor.name || "the tutor";
      setPayTutorId(null);
      setToast(`Payment recorded for ${paidName}`);
      loadTutors();
      reloadCycle();
      loadHistory();
    } catch (e) {
      if (e?.code === "functions/already-exists") {
        // An earlier try of this same upload landed; its answer was lost. Nothing is left to do.
        setPayTutorId(null);
        setToast("This payment was already recorded.");
        loadTutors();
        reloadCycle();
        loadHistory();
        return;
      }
      // The callable's own message ("This receipt is already recorded…", "That is not a pay
      // cycle that has ended.") — the reason the save was refused. The dialog stays open.
      setSaveError(e?.message || "Could not record the payment.");
    } finally {
      setSaving(false);
    }
  };

  // ── Undo (053, unchanged; opened from the panel or the history) ──
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoReason, setUndoReason] = useState("");
  const [undoSaving, setUndoSaving] = useState(false);
  const [undoError, setUndoError] = useState("");

  const openUndo = (p, teacherName) => {
    setUndoTarget({ ...p, teacherName });
    setUndoReason("");
    setUndoError("");
  };
  const closeUndo = () => {
    if (undoSaving) return;
    setUndoTarget(null);
  };
  const confirmUndo = async () => {
    if (!undoTarget) return;
    setUndoSaving(true);
    setUndoError("");
    try {
      await undoPayout({ payoutId: undoTarget.id, reason: undoReason.trim() || undefined });
      setUndoTarget(null);
      setToast("Payment undone");
      loadTutors();
      reloadCycle();
      loadHistory();
    } catch (e) {
      setUndoError(e?.message || "Could not undo this payment.");
    } finally {
      setUndoSaving(false);
    }
  };

  const showReceipt = (path) => openReceipt(path).catch((e) => setToast(e.message));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <ListHead caption="Money" title="Tutor payouts" subtitle={headLine} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          {cycles.length > 0 && (
            <ToolbarMenu
              id="cycle"
              label="Cycle"
              value={cycle?.key}
              options={cycles.map((c) => ({ key: c.key, label: c.label }))}
              onChange={chooseCycle}
            />
          )}
          <Button onClick={doExport} disabled={!cycle || !rowsReady} disableRipple disableFocusRipple sx={headButtonSx}>
            <RiDownload2Line size={18} />
            Export
          </Button>
        </Box>
      </Box>

      {cyclesReady && cycles.length === 0 ? (
        <TableCard title="Pay for this cycle">
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
              No pay cycle has ended yet.
            </Typography>
          </Box>
        </TableCard>
      ) : (
        <>
          {cycle && <CycleCard cycle={cycle} summary={summary} failed={rowsFailed} owed={owedView} />}
          <TableCard
            toolbar={
              <>
                <SegmentTabs label="Pay status" tabs={PAY_TABS} value={tab} counts={rowsReady ? payTabCounts(rows) : {}} onChange={setTab} />
                <Box sx={{ flex: 1, minWidth: 12 }} />
                <SearchField value={search} onChange={setSearch} placeholder="Search tutor" />
              </>
            }
          >
            <TableContainer>
              <Table aria-busy={rowsLoading}>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((c, i) => (
                      <TableCell key={i}>{c}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsLoading ? (
                    <SkeletonRows columns={COLUMNS.length} />
                  ) : rowsFailed || shown.length === 0 ? (
                    <TableMessageRow
                      colSpan={COLUMNS.length}
                      error={rowsFailed ? "Could not load this cycle's pay" : ""}
                      errorMessage={tutorsError}
                      onRetry={retryRows}
                      emptyText={rows.length === 0 ? "Nobody earned or was paid anything in this cycle." : "No tutor matches this tab and search."}
                    />
                  ) : (
                    shown.map((r) => {
                      const name = r.tutor.name || r.tutor.id;
                      return (
                        <TableRow
                          key={r.tutor.id}
                          hover
                          selected={r.tutor.id === openTutorId}
                          onClick={() => openPanel(r.tutor.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <TutorCell tutor={r.tutor} />
                          </TableCell>
                          <TableCell>
                            <TwoLineCell top={countText(r.lessons)} bottom={hoursText(r.hours)} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{egp(r.teaching)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{r.fixed > 0 ? egp(r.fixed) : "—"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>{egp(r.total)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{r.paid > 0 ? egp(r.paid) : "—"}</TableCell>
                          <TableCell>
                            <StatusChip label={r.status.label} color={r.status.color} />
                          </TableCell>
                          {/* The row opens the panel; the eye must not open it a second time. */}
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <RowAction Icon={RiEyeLine} label={`Open ${name}'s payout`} onClick={() => openPanel(r.tutor.id)} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableCard>
        </>
      )}

      <TableCard title="Payout history" subtitle="The latest 100 payments">
        <TableContainer>
          <Table aria-busy={historyLoading}>
            <TableHead>
              <TableRow>
                {historyColumns.map((c) => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tutorsError ? (
                <TableMessageRow
                  colSpan={historyColumns.length}
                  error="Could not load tutors"
                  onRetry={() => {
                    loadTutors();
                    if (historyError) loadHistory();
                  }}
                />
              ) : historyLoading || tutors === null ? (
                <SkeletonRows columns={historyColumns.length} />
              ) : historyError || historyRows.length === 0 ? (
                <TableMessageRow
                  colSpan={historyColumns.length}
                  error={historyError ? "Could not load payout history" : ""}
                  onRetry={loadHistory}
                  emptyText="No payments recorded yet."
                />
              ) : (
                historyRows.map((p) => {
                  const when = dateTimeLines(p.createdAt);
                  const creator = creatorNames[p.createdBy];
                  const teacherName = p.teacherName || tutorsById[p.teacherId]?.name || p.teacherId;
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <TwoLineCell top={when ? when.date : "—"} bottom={when?.time} />
                      </TableCell>
                      <TableCell>{teacherName}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{payoutCycleLabel(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {egp(p.amount)}
                        {p.undoneAt && <StatusChip label="Undone" color="default" />}
                      </TableCell>
                      <TableCell>
                        {p.receiptPath ? (
                          <Link component="button" type="button" onClick={() => showReceipt(p.receiptPath)}>
                            {p.receiptName || "Receipt"}
                          </Link>
                        ) : (
                          p.paymentReference || "—"
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{p.unpaidBefore == null ? "—" : egp(p.unpaidBefore)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{p.unpaidAfter == null ? "—" : egp(p.unpaidAfter)}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <TwoLineCell wrap top={p.notes || "—"} />
                      </TableCell>
                      <TableCell>{creator?.name || creator?.email || "—"}</TableCell>
                      {canUndo && (
                        <TableCell>
                          {p.cycleKey && !p.undoneAt && (
                            <SmallButton tone="outline" onClick={() => openUndo(p, teacherName)}>
                              Undo
                            </SmallButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <PayoutPanel
        row={panelRow}
        busy={rowsLoading}
        cycle={cycle}
        bookings={bookings}
        payments={cyclePayouts}
        canPay={canPay}
        canUndo={canUndo}
        canOpenTutor={canOpenTutor}
        onClose={closePanel}
        onPay={openPay}
        onUndo={openUndo}
        onReceipt={showReceipt}
      />

      <Dialog open={Boolean(payRow)} onClose={closePay} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER_SX}>
        <DialogTitle style={{ fontWeight: 600 }}>Mark as paid</DialogTitle>
        <DialogContent dividers>
          {payRow && cycle && (
            <>
              <DetailRows
                rows={[
                  { key: "tutor", label: "Tutor", value: payRow.tutor.name || payRow.tutor.id },
                  { key: "cycle", label: "Cycle", value: cycle.label },
                  { key: "total", label: "Total", value: egp(payRow.total) },
                  { key: "paidSoFar", label: "Paid so far", value: egp(payRow.paid) },
                  { key: "left", label: "Left", value: egp(payRow.status.left) },
                ]}
              />
              {!owedMoves(cycle) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Owed does not change: the opening balance already cleared this cycle.
                </Alert>
              )}
              {saveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {saveError}
                </Alert>
              )}
              <TextField
                label="Amount (E£)"
                type="number"
                fullWidth
                margin="normal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => setAmountTouched(true)}
                error={amountTouched && Boolean(amountProblem(amount, payRow.status.left))}
                helperText={amountTouched ? amountProblem(amount, payRow.status.left) : " "}
              />
              <input
                ref={fileInput}
                type="file"
                accept={RECEIPT_TYPES.join(",")}
                hidden
                onChange={(e) => {
                  setFile(e.target.files[0] || null);
                  setFileTouched(true);
                  setUploadedPath(null);
                }}
              />
              <Button variant="outlined" sx={{ mt: 1 }} onClick={() => fileInput.current?.click()}>
                Choose receipt
              </Button>
              {file && (
                <Typography variant="bodySmall" sx={{ mt: "8px", color: tokens.text.secondary }}>
                  {file.name}
                </Typography>
              )}
              {fileTouched && receiptProblem(file) && (
                <Typography variant="labelMedium" component="div" sx={{ mt: "4px", color: tokens.error.ink }}>
                  {receiptProblem(file)}
                </Typography>
              )}
              <TextField
                label="Note (staff only)"
                fullWidth
                multiline
                minRows={2}
                margin="normal"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                inputProps={{ maxLength: NOTE_MAX }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePay} disabled={saving} sx={ACTION_BUTTON_SX}>
            Cancel
          </Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={ACTION_BUTTON_SX}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(undoTarget)} onClose={closeUndo} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER_SX}>
        <DialogTitle style={{ fontWeight: 600 }}>Undo this payment?</DialogTitle>
        <DialogContent dividers>
          {undoTarget && (
            <>
              <Typography variant="bodySmall" sx={{ color: tokens.text.primary, mb: "8px" }}>
                {egp(undoTarget.amount)} to {undoTarget.teacherName || undoTarget.teacherId} for{" "}
                {payoutCycleLabel(undoTarget)}.
              </Typography>
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary, mb: "8px" }}>
                {undoTarget.owedMoved ? `Owed goes back up by ${egp(undoTarget.amount)}.` : "Owed does not change."}
              </Typography>
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary, mb: "16px" }}>
                The tutor is not notified.
              </Typography>
              {undoError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {undoError}
                </Alert>
              )}
              <TextField
                label="Reason (optional)"
                fullWidth
                multiline
                minRows={2}
                value={undoReason}
                onChange={(e) => setUndoReason(e.target.value.slice(0, REASON_MAX))}
                inputProps={{ maxLength: REASON_MAX }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUndo} disabled={undoSaving} sx={ACTION_BUTTON_SX}>
            Cancel
          </Button>
          <Button onClick={confirmUndo} variant="contained" color="error" disabled={undoSaving} sx={ACTION_BUTTON_SX}>
            {undoSaving ? "Undoing…" : "Undo payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast("")} message={toast} />
    </Box>
  );
};

export default Payouts;
